import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import Layout from '../components/Layout';
import AssessmentTabs from '../components/AssessmentTabs';
import Avatar from '../components/Avatar';
import { getOnboardingCandidates, PortalCandidate } from '../api/onboardingService';
import { updatePrepareEnrichStatus } from '../api/prepareEnrichService';
import { getImageUrl } from '../utils/imageUrl';
import { calculateOnboardingProgress } from '../utils/progress';

const Onboarding: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);

  const urlPortalBucket = searchParams.get('portalBucket') || '';
  const urlPrePlanterOnly = searchParams.get('prePlanterOnly') === 'true';
  const pipelineListMode = Boolean(urlPortalBucket || urlPrePlanterOnly);
  const [showPrepareEnrichModal, setShowPrepareEnrichModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<PortalCandidate | null>(null);
  const [prepareEnrichStatus, setPrepareEnrichStatus] = useState('');
  const [prepareEnrichNotes, setPrepareEnrichNotes] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: [
      'onboardingCandidates',
      {
        status: statusFilter,
        portalBucket: urlPortalBucket,
        prePlanterOnly: urlPrePlanterOnly,
        page,
        limit: 20,
      },
    ],
    queryFn: () =>
      getOnboardingCandidates({
        status: statusFilter || undefined,
        portalBucket: urlPortalBucket || undefined,
        prePlanterOnly: urlPrePlanterOnly || undefined,
        page,
        limit: 20,
      }),
    retry: false,
  });

  const allCandidates = data?.data || [];
  const pagination = data?.pagination;
  
  const preAssessmentStatuses = new Set(['exploring', 'preassessment', 'postponed', 'archived']);

  const candidates = pipelineListMode
    ? allCandidates
    : statusFilter === 'archived'
      ? allCandidates.filter((c) => c.portalStatus === 'archived')
      : statusFilter
        ? allCandidates.filter((c) => c.portalStatus === statusFilter)
        : allCandidates.filter(
            (c) => preAssessmentStatuses.has(c.portalStatus || '') && c.portalStatus !== 'archived',
          );

  const archivedCandidates =
    pipelineListMode || statusFilter !== ''
      ? []
      : allCandidates.filter((c) => c.portalStatus === 'archived');

  const updatePrepareEnrichMutation = useMutation({
    mutationFn: ({ id, status, notes }: { id: number; status: string; notes?: string }) =>
      updatePrepareEnrichStatus(id, { status, notes }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['onboardingCandidates'] });
      setShowPrepareEnrichModal(false);
      setSelectedCandidate(null);
      setPrepareEnrichStatus('');
      setPrepareEnrichNotes('');
    },
  });

  const getStatusBadgeColor = (status: string) => {
    const colors: Record<string, string> = {
      exploring: 'bg-blue-100 text-blue-800',
      preassessment: 'bg-yellow-100 text-yellow-800',
      postponed: 'bg-orange-100 text-orange-800',
      archived: 'bg-gray-100 text-gray-600',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      exploring: 'Exploring',
      preassessment: 'Pre-Assessment',
      postponed: 'Postponed',
      archived: 'Archived',
    };
    return labels[status] || status;
  };

  const getPortalProgressPercent = (candidate: PortalCandidate) =>
    typeof candidate.portalProgressPercentage === 'number' ? candidate.portalProgressPercentage : calculateOnboardingProgress(candidate);

  const getTeamProgressPercent = (candidate: PortalCandidate) =>
    typeof candidate.teamProgressPercentage === 'number' ? candidate.teamProgressPercentage : 0;

  const openPrepareEnrichModal = (candidate: PortalCandidate, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelectedCandidate(candidate);
    setPrepareEnrichStatus(candidate.prepareEnrichStatus || 'not_started');
    setPrepareEnrichNotes(candidate.prepareEnrichNotes || '');
    setShowPrepareEnrichModal(true);
  };

  return (
    <Layout>
      <div className="px-4 sm:px-0">
        {/* Header */}
        <div className="sm:flex sm:items-center sm:justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Pre-Assessment Candidates</h1>
            <p className="mt-2 text-sm text-gray-600">
              Review and manage candidates in the pre-assessment process
            </p>
          </div>
        </div>

        {pipelineListMode && (
          <div className="mb-4 rounded-lg border border-primary-100 bg-primary-50 px-4 py-3 text-sm text-primary-900">
            Filtered from pipeline overview (portal candidates without a church planter profile).{' '}
            <Link to="/planters/pipeline" className="font-medium underline hover:no-underline">
              Back to pipeline
            </Link>
          </div>
        )}

        <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 mb-6">
          <AssessmentTabs />
        </div>

        {/* Filters */}
        <div className="bg-white shadow rounded-lg mb-6">
          <div className="px-4 py-5 sm:p-6">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              {/* Status Filter */}
              <div>
                <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-1">
                  Candidate Status
                </label>
                <select
                  id="status"
                  value={statusFilter}
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setPage(1);
                  }}
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                >
                  <option value="">Active Candidates</option>
                  <option value="exploring">Exploring</option>
                  <option value="preassessment">Pre-Assessment</option>
                  <option value="postponed">Postponed</option>
                  <option value="archived">Archived</option>
                </select>
              </div>

              {/* Stats */}
              <div className="flex items-end justify-end space-x-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-gray-900">
                    {candidates.length}
                  </div>
                  <div className="text-xs text-gray-500">
                    Candidates
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Candidates List */}
        {isLoading ? (
          <div className="bg-white shadow rounded-lg p-8">
            <div className="flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <span className="ml-3 text-gray-600">Loading candidates...</span>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white shadow rounded-lg p-8">
            <div className="text-center">
              <div className="text-yellow-600 mb-2">
                <svg className="mx-auto h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Backend Endpoint Needed</h3>
              <p className="text-sm text-gray-600 mb-4">
                The admin endpoint for viewing portal candidates needs to be added to the backend.
              </p>
              <p className="text-xs text-gray-500">
                Add GET /api/admin/portal-candidates endpoint to enable this feature.
              </p>
            </div>
          </div>
        ) : candidates.length === 0 ? (
          <div className="bg-white shadow rounded-lg p-8">
            <div className="text-center text-gray-500">
              <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
              </svg>
              <p>No pre-assessment candidates found.</p>
              <p className="text-sm mt-2">Candidates in the pre-assessment process will appear here.</p>
            </div>
          </div>
        ) : (
          <>
            {/* Status Legend */}
            <div className="bg-white shadow rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <h3 className="text-sm font-medium text-gray-700">Status Legend:</h3>
                <div className="flex items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-200/40 rounded border border-green-300"></div>
                    <span className="text-sm text-gray-600">Exploring</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-white rounded border border-gray-300"></div>
                    <span className="text-sm text-gray-600">Pre-Assessment</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-yellow-200/50 rounded border border-yellow-300"></div>
                    <span className="text-sm text-gray-600">Postponed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Cards Grid */}
            <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {candidates.map((candidate: PortalCandidate) => (
                <div 
                  key={candidate.id}
                  className="bg-white shadow rounded-lg overflow-hidden hover:shadow-lg transition-shadow cursor-pointer relative"
                  onClick={() => navigate(`/onboarding/${candidate.id}`)}
                >
                  {/* Status Overlay - transparent colored mask */}
                  {candidate.portalStatus === 'exploring' && (
                    <div className="absolute inset-0 bg-green-200/40 pointer-events-none z-10 rounded-lg" />
                  )}
                  {candidate.portalStatus === 'postponed' && (
                    <div className="absolute inset-0 bg-yellow-200/50 pointer-events-none z-10 rounded-lg" />
                  )}
                  
                  {/* Header with image */}
                  <div className="h-32 relative overflow-hidden">
                    {/* Background - Family photo or gradient */}
                    {candidate.familyPhotoImagePath ? (
                      <>
                        <img 
                          src={getImageUrl(candidate.familyPhotoImagePath, { width: 1200, quality: 70, format: 'webp' })}
                          alt="Family"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        {/* Overlay for better contrast */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-black/20" />
                      </>
                    ) : (
                      <div className="absolute inset-0 bg-gradient-to-r from-primary-400 to-primary-600" />
                    )}
                    
                    {/* Avatar */}
                    <div className="absolute bottom-0 left-4 border-4 border-white rounded-full">
                      <Avatar 
                        name={candidate.name}
                        imageUrl={getImageUrl(candidate.headshotImagePath, { width: 128, quality: 70, format: 'webp' })}
                        size="xl"
                      />
                    </div>
                  </div>

                  {/* Content */}
                  <div className="pt-14 px-4 pb-4">
                    <h3 className="text-lg font-semibold text-gray-900 mb-1">
                      {candidate.name}
                    </h3>
                    
                    {/* Contact Information */}
                    <div className="space-y-1 mb-3">
                      {candidate.email && (
                        <div className="flex items-start text-sm text-gray-600">
                          <svg className="h-4 w-4 mr-1.5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs text-gray-500 block">Candidate</span>
                            <span className="truncate block">{candidate.email}</span>
                          </div>
                        </div>
                      )}
                      {candidate.wifeEmail && (
                        <div className="flex items-start text-sm text-gray-600">
                          <svg className="h-4 w-4 mr-1.5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs text-gray-500 block">Wife</span>
                            <span className="truncate block">{candidate.wifeEmail}</span>
                          </div>
                        </div>
                      )}
                      {candidate.phone && (
                        <div className="flex items-start text-sm text-gray-600">
                          <svg className="h-4 w-4 mr-1.5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs text-gray-500 block">Candidate</span>
                            <span className="block">{candidate.phone}</span>
                          </div>
                        </div>
                      )}
                      {candidate.wifePhone && (
                        <div className="flex items-start text-sm text-gray-600">
                          <svg className="h-4 w-4 mr-1.5 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                          <div className="flex-1 min-w-0">
                            <span className="text-xs text-gray-500 block">Wife</span>
                            <span className="block">{candidate.wifePhone}</span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Completion Icons */}
                    <div className="flex items-center gap-2 mb-3 flex-wrap">
                      {/* Basic Info */}
                      <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                        candidate.onboardingStep >= 2 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                        <span>Info</span>
                      </div>

                      {/* Photos */}
                      <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                        candidate.onboardingStep >= 5 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
                      }`}>
                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
                        </svg>
                        <span>Photos</span>
                      </div>

                      {/* P&E */}
                      {candidate.maritalStatus === 'married' && (
                        <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                          candidate.prepareEnrichStatus === 'complete' ? 'bg-green-100 text-green-700' :
                          candidate.prepareEnrichStatus === 'scheduled' || candidate.prepareEnrichStatus === 'started' ? 'bg-yellow-100 text-yellow-700' :
                          'bg-gray-100 text-gray-500'
                        }`}>
                          <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          <span>P&E</span>
                        </div>
                      )}

                      {/* Spiritual Health */}
                      <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                        candidate.spiritualHealthCompleted ? 'bg-green-100 text-green-700' :
                        (candidate.spiritualHealthCandidateSubmitted || candidate.spiritualHealthSpouseSubmitted) ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M10 3.5a1.5 1.5 0 013 0V4a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-.5a1.5 1.5 0 000 3h.5a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-.5a1.5 1.5 0 00-3 0v.5a1 1 0 01-1 1H6a1 1 0 01-1-1v-3a1 1 0 00-1-1h-.5a1.5 1.5 0 010-3H4a1 1 0 001-1V6a1 1 0 011-1h3a1 1 0 001-1v-.5z" />
                        </svg>
                        <span>Spiritual</span>
                      </div>

                      {/* Background Check */}
                      <div className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${
                        candidate.backgroundCheckStatus === 'received' ? 'bg-green-100 text-green-700' :
                        candidate.backgroundCheckStatus === 'submitted' || candidate.backgroundCheckStatus === 'started' ? 'bg-yellow-100 text-yellow-700' :
                        'bg-gray-100 text-gray-500'
                      }`}>
                        <svg className="h-3 w-3" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                        <span>BGC</span>
                      </div>
                    </div>

                    {/* Progress */}
                    <div className="mb-3">
                      <div className="space-y-2">
                        <div>
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span className="font-semibold uppercase tracking-wide">Portal</span>
                            <span>{getPortalProgressPercent(candidate)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-primary-600 h-2 rounded-full transition-all"
                              style={{ width: `${getPortalProgressPercent(candidate)}%` }}
                            />
                          </div>
                        </div>
                        <div>
                          <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                            <span className="font-semibold uppercase tracking-wide">Team</span>
                            <span>{getTeamProgressPercent(candidate)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-emerald-600 h-2 rounded-full transition-all"
                              style={{ width: `${getTeamProgressPercent(candidate)}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {candidate.portalStatus && (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(candidate.portalStatus)}`}>
                          {getStatusLabel(candidate.portalStatus)}
                        </span>
                      )}
                      
                      {/* P&E Update Button */}
                      {candidate.maritalStatus === 'married' && (
                        <button
                          onClick={(e) => openPrepareEnrichModal(candidate, e)}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded"
                        >
                          Update P&E
                        </button>
                      )}
                    </div>

                    {/* Additional Info */}
                    <div className="mt-3 pt-3 border-t border-gray-200 text-xs text-gray-500">
                      <div className="flex items-center justify-between">
                        <span>Submitted: {new Date(candidate.createdAt).toLocaleDateString()}</span>
                        {candidate.onboardingCompleted && (
                          <svg className="h-4 w-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Footer Actions */}
                  <div className="bg-gray-50 px-4 py-3 flex justify-end space-x-2">
                    <button 
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/onboarding/${candidate.id}`);
                      }}
                      className="text-sm text-primary-600 hover:text-primary-700 font-medium"
                    >
                      View Details
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {pagination && pagination.pages > 1 && (
              <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6 mt-6 rounded-lg shadow">
                <div className="flex-1 flex justify-between sm:hidden">
                  <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1}
                    className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                    disabled={page === pagination.pages}
                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
                <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                  <div>
                    <p className="text-sm text-gray-700">
                      Showing <span className="font-medium">{((page - 1) * (pagination.limit || 20)) + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(page * (pagination.limit || 20), pagination.total)}</span> of{' '}
                      <span className="font-medium">{pagination.total}</span> results
                    </p>
                  </div>
                  <div>
                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                      <button
                        onClick={() => setPage(Math.max(1, page - 1))}
                        disabled={page === 1}
                        className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Previous</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                      <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                        Page {page} of {pagination.pages}
                      </span>
                      <button
                        onClick={() => setPage(Math.min(pagination.pages, page + 1))}
                        disabled={page === pagination.pages}
                        className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <span className="sr-only">Next</span>
                        <svg className="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                          <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                        </svg>
                      </button>
                    </nav>
                  </div>
                </div>
              </div>
            )}

            {/* Archived Candidates Section */}
            {archivedCandidates.length > 0 && (
              <div className="mt-12">
                <div className="mb-6">
                  <h2 className="text-xl font-semibold text-gray-900">Archived Candidates</h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {archivedCandidates.length} archived {archivedCandidates.length === 1 ? 'candidate' : 'candidates'}
                  </p>
                </div>
                
                <div className="bg-white shadow rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Candidate
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Contact
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Progress
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Submitted
                        </th>
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {archivedCandidates.map((candidate) => (
                        <tr key={candidate.id} className="hover:bg-gray-50 opacity-60">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0">
                                <Avatar 
                                  name={candidate.name}
                                  imageUrl={getImageUrl(candidate.headshotImagePath, { width: 96, quality: 70, format: 'webp' })}
                                  size="md"
                                />
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {candidate.name}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600">{candidate.email}</div>
                            <div className="text-sm text-gray-500">{candidate.phone}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-600">
                              {getPortalProgressPercent(candidate)}%
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Date(candidate.createdAt).toLocaleDateString()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <button
                              onClick={() => navigate(`/onboarding/${candidate.id}`)}
                              className="text-primary-600 hover:text-primary-900"
                            >
                              View
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}

        {/* Prepare/Enrich Update Modal */}
        {showPrepareEnrichModal && selectedCandidate && (
          <div className="fixed z-[60] inset-0 overflow-y-auto">
            <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
              <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowPrepareEnrichModal(false)}></div>
              <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>
              <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
                    Update Prepare/Enrich Status
                  </h3>
                  
                  <div className="mb-4">
                    <p className="text-sm text-gray-600 mb-2">
                      Candidate: <span className="font-medium">{selectedCandidate.name}</span>
                    </p>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="prepareEnrichStatus" className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      id="prepareEnrichStatus"
                      value={prepareEnrichStatus}
                      onChange={(e) => setPrepareEnrichStatus(e.target.value)}
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    >
                      <option value="not_started">Not Started</option>
                      <option value="started">Started</option>
                      <option value="scheduled">Scheduled</option>
                      <option value="complete">Complete</option>
                    </select>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="prepareEnrichNotes" className="block text-sm font-medium text-gray-700 mb-1">
                      Notes (Optional)
                    </label>
                    <textarea
                      id="prepareEnrichNotes"
                      rows={4}
                      value={prepareEnrichNotes}
                      onChange={(e) => setPrepareEnrichNotes(e.target.value)}
                      placeholder="Add any notes about this Prepare/Enrich session..."
                      className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
                    />
                  </div>
                </div>
                <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    disabled={updatePrepareEnrichMutation.isPending}
                    onClick={() => updatePrepareEnrichMutation.mutate({ 
                      id: selectedCandidate.id,
                      status: prepareEnrichStatus, 
                      notes: prepareEnrichNotes || undefined 
                    })}
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 text-base font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                  >
                    {updatePrepareEnrichMutation.isPending ? 'Updating...' : 'Update'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowPrepareEnrichModal(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Onboarding;
